"use client"

import { useState, useEffect } from "react"
import { Card, Modal, Alert } from "react-bootstrap"
import { useTranslation } from "../../hooks/use-translation"
import { useAuth } from "../../contexts/auth-context"
import apiClient from "../../api/apiClient"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { fetchBuildings } from "../../redux/slices/buildingsSlice"

export default function AccountPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const dispatch = useAppDispatch()
  const { buildings } = useAppSelector((state) => state.buildings)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showSuccessAlert, setShowSuccessAlert] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch buildings data
  useEffect(() => {
    dispatch(fetchBuildings(false)) // Only fetch active buildings
  }, [dispatch])

  // Function to get building name from building ID
  const getBuildingName = (buildingId: string) => {
    if (!buildings || buildings.length === 0) {
      return "Loading..."
    }
    const building = buildings.find((b) => b.id === buildingId)
    return building ? building.name : buildingId
  }

  const handleDeleteAccount = async () => {
    setIsDeleting(true)
    setError(null)

    try {
      await apiClient.post("/users/delete-account-request")
      setShowDeleteModal(false)
      setShowSuccessAlert(true)
    } catch (error: any) {
      console.error("Delete account request error:", error)
      setError(error.response?.data?.error || t("failedToRequestAccountDeletion"))
    } finally {
      setIsDeleting(false)
    }
  }

  if (!user) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
        <div className="text-muted">{t("loading")}</div>
      </div>
    )
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1 className="fs-2 fw-bold mb-0 text-white">{t("accountInformation")}</h1>
          </div>

          {showSuccessAlert && (
            <Alert variant="success" dismissible onClose={() => setShowSuccessAlert(false)} className="mb-4">
              <Alert.Heading>{t("requestSubmitted")}</Alert.Heading>
              <p className="mb-0">{t("accountDeletionRequestMessage")}</p>
            </Alert>
          )}

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
              {error}
            </Alert>
          )}

          <Card>
            <Card.Body className="p-4">
              <div className="row">
                <div className="col-md-4 text-center mb-4 mb-md-0">
                  <div className="mb-3">
                    {user.profilePhotoUrl ? (
                      <img
                        src={user.profilePhotoUrl || "/placeholder.svg"}
                        alt={user.fullName}
                        className="rounded-circle"
                        style={{ width: "120px", height: "120px", objectFit: "cover" }}
                      />
                    ) : (
                      <div
                        className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                        style={{ width: "120px", height: "120px", margin: "0 auto" }}
                      >
                        <span className="text-muted fs-1">{user.fullName?.charAt(0)?.toUpperCase() || "U"}</span>
                      </div>
                    )}
                  </div>
                  <h5 className="mb-1">{user.fullName}</h5>
                </div>

                <div className="col-md-8">
                  <div className="row g-3">
                    <div className="col-sm-6">
                      <label className="form-label text-muted small">{t("fullName")}</label>
                      <div className="form-control-plaintext border rounded px-3 py-2 bg-light">{user.fullName}</div>
                    </div>

                    <div className="col-sm-6">
                      <label className="form-label text-muted small">{t("emailAddress")}</label>
                      <div className="form-control-plaintext border rounded px-3 py-2 bg-light">{user.email}</div>
                    </div>

                    <div className="col-sm-6">
                      <label className="form-label text-muted small">{t("phoneNumber")}</label>
                      <div className="form-control-plaintext border rounded px-3 py-2 bg-light">
                        {user.phoneNumber || t("notProvided")}
                      </div>
                    </div>

                    <div className="col-sm-6">
                      <label className="form-label text-muted small">{t("building")}</label>
                      <div className="form-control-plaintext border rounded px-3 py-2 bg-light">
                        {getBuildingName(user.building)}
                      </div>
                    </div>

                    <div className="col-sm-6">
                      <label className="form-label text-muted small">{t("roomNumber")}</label>
                      <div className="form-control-plaintext border rounded px-3 py-2 bg-light">{user.roomNumber}</div>
                    </div>

                    <div className="col-sm-6">
                      <label className="form-label text-muted small">{t("role")}</label>
                      <div className="form-control-plaintext border rounded px-3 py-2 bg-light">
                        <span className={`badge ${user.role === "admin" ? "bg-primary" : "bg-secondary"}`}>
                          {t(user.role)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <hr className="my-4" />

              <div className="text-center">
                <button
                  type="button"
                  className="btn btn-link text-danger p-0"
                  style={{ textDecoration: "none", fontSize: "0.9rem" }}
                  onClick={() => setShowDeleteModal(true)}
                >
                  {t("deleteMyAccount")}
                </button>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("deleteAccountConfirmation")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t("deleteAccountWarning")}</p>
          <p className="text-muted small">{t("deleteAccountProcess")}</p>
        </Modal.Body>
        <Modal.Footer>
          <button className="modal-cancel-btn" onClick={() => setShowDeleteModal(false)} disabled={isDeleting}>
            {t("cancel")}
          </button>
          <button className="modal-delete-btn" onClick={handleDeleteAccount} disabled={isDeleting}>
            {isDeleting ? t("submitting") : t("requestAccountDeletion")}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
